import React, { useState, useEffect, useMemo } from "react";
import { investorLogin, investorVerify } from "../lib/api";
import { toast } from "sonner";
import { Lock, Unlock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";

const STORAGE_KEY = "riviera_investor_token";

export default function InvestorMode() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [pass, setPass] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    investorVerify(token).then(() => setAuthed(true)).catch(() => { localStorage.removeItem(STORAGE_KEY); setToken(""); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token: t } = await investorLogin(pass);
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t); setAuthed(true);
    } catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };

  if (!authed) {
    return (
      <main className="pt-32 min-h-[80vh]" data-testid="page-investor-mode-locked">
        <div className="container-luxe py-16 max-w-md">
          <div className="overline">PRIVATE</div>
          <h1 className="font-display text-4xl text-[var(--ink)] mt-4 font-light">Investor Mode</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Confidential — password required.</p>
          <form onSubmit={submit} className="mt-8 space-y-3">
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Password" className="input-luxe" data-testid="investor-password" />
            <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="investor-submit">
              <Lock size={14} /> Unlock
            </button>
          </form>
        </div>
      </main>
    );
  }

  return <InvestorDashboard token={token} onLogout={() => { localStorage.removeItem(STORAGE_KEY); setAuthed(false); setToken(""); }} />;
}

function InvestorDashboard({ onLogout }) {
  const [sessions, setSessions] = useState(2);     // per day
  const [days, setDays] = useState(180);           // operational days/year
  const [avg, setAvg] = useState(680);             // avg ticket €
  const [units, setUnits] = useState(1);           // Exomobil units
  const [margin, setMargin] = useState(0.62);      // gross margin

  const revenue = useMemo(() => sessions * days * avg * units, [sessions, days, avg, units]);
  const gross = revenue * margin;
  const projection = useMemo(() => {
    const arr = [];
    for (let y = 1; y <= 5; y++) {
      const u = Math.min(units * Math.pow(1.6, y - 1), 12); // cap
      const r = sessions * days * avg * u;
      arr.push({ year: `Y${y}`, revenue: Math.round(r), gross: Math.round(r * margin) });
    }
    return arr;
  }, [sessions, days, avg, units, margin]);

  const market = [
    { name: "Monaco HNWI", v: 320 },
    { name: "Yacht season", v: 540 },
    { name: "GP weekend", v: 180 },
    { name: "Cannes/St-Tropez", v: 420 },
  ];

  return (
    <main className="pt-28 pb-24" data-testid="page-investor-mode">
      <div className="container-luxe">
        <div className="flex items-center justify-between hairline-b pb-6">
          <div>
            <div className="overline">INVESTOR MODE · CONTROL ROOM</div>
            <h1 className="font-display text-3xl text-[var(--ink)] mt-2 font-light">ROI Simulator & Market Model</h1>
          </div>
          <button onClick={onLogout} className="btn-ghost" data-testid="investor-logout"><Unlock size={14} /> Logout</button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px hairline mt-8">
          {[
            { k: "Annual Revenue", v: `€${revenue.toLocaleString()}` },
            { k: "Gross Profit", v: `€${Math.round(gross).toLocaleString()}` },
            { k: "Margin", v: `${Math.round(margin * 100)}%` },
            { k: "Units", v: units },
          ].map((kpi) => (
            <div key={kpi.k} className="bg-surface-1 p-6">
              <div className="overline">{kpi.k}</div>
              <div className="font-display text-2xl md:text-3xl text-[var(--ink)] mt-2 font-light">{kpi.v}</div>
            </div>
          ))}
        </div>

        {/* Sliders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px hairline mt-px">
          <div className="bg-surface-1 p-6 space-y-5">
            <Slider label="Sessions / day" min={1} max={8} step={1} value={sessions} onChange={setSessions} testId="slider-sessions" />
            <Slider label="Operational days / year" min={120} max={300} step={10} value={days} onChange={setDays} testId="slider-days" />
            <Slider label="Avg ticket (€)" min={300} max={1500} step={20} value={avg} onChange={setAvg} testId="slider-avg" />
          </div>
          <div className="bg-surface-1 p-6 space-y-5">
            <Slider label="Exomobil units" min={1} max={6} step={1} value={units} onChange={setUnits} testId="slider-units" />
            <Slider label="Gross margin" min={0.4} max={0.8} step={0.01} value={margin} onChange={setMargin} format={(v) => `${Math.round(v * 100)}%`} testId="slider-margin" />
            <div className="text-xs text-[var(--ink)]/50 pt-2">Levée cible : <span className="text-[var(--ink)]">€1.2M</span> · 24-month runway · Monaco HQ.</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px hairline mt-px">
          <div className="bg-surface-1 p-6">
            <div className="overline mb-4">5Y Projection</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={projection}>
                  <CartesianGrid stroke="#E2DBC9" strokeDasharray="2 4" />
                  <XAxis dataKey="year" stroke="#6B6B6B" fontSize={11} />
                  <YAxis stroke="#6B6B6B" fontSize={11} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2DBC9", color: "#1A1A20" }} formatter={(v) => `€${v.toLocaleString()}`} />
                  <Line dataKey="revenue" stroke="#A8852C" strokeWidth={2} dot={{ fill: "#A8852C" }} />
                  <Line dataKey="gross" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-surface-1 p-6">
            <div className="overline mb-4">Market Segments (€M, addressable)</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={market}>
                  <CartesianGrid stroke="#E2DBC9" strokeDasharray="2 4" />
                  <XAxis dataKey="name" stroke="#6B6B6B" fontSize={11} />
                  <YAxis stroke="#6B6B6B" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2DBC9", color: "#1A1A20" }} />
                  <Bar dataKey="v" fill="#A8852C" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* GP Model */}
        <div className="hairline mt-px p-8 bg-surface-1">
          <div className="overline">MONACO GP MODEL</div>
          <h2 className="font-display text-2xl text-[var(--ink)] mt-3 font-light">Cellule mobile · 5 jours · marges premium</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px hairline mt-6">
            {[
              { k: "Days", v: "5" }, { k: "Sessions", v: "48" },
              { k: "Avg ticket", v: "€1 050" }, { k: "GP revenue", v: "€504k" },
            ].map((m) => (
              <div key={m.k} className="bg-base p-5">
                <div className="overline">{m.k}</div>
                <div className="font-display text-2xl text-[var(--ink)] mt-1 font-light">{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scalability */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px hairline mt-px">
          {[
            { k: "Y1 — Monaco", v: "1 unit · proof" },
            { k: "Y2 — Riviera", v: "3 units · Cannes/StTropez" },
            { k: "Y3 — Mediterranean", v: "6 units · Ibiza/Mykonos" },
          ].map((s) => (
            <div key={s.k} className="bg-surface-1 p-7">
              <div className="overline">{s.k}</div>
              <div className="font-display text-lg text-[var(--ink)] mt-2 font-light">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

function Slider({ label, min, max, step, value, onChange, format, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <span className="font-display text-sm text-[var(--ink)]">{format ? format(value) : value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[var(--gold)]"
      />
    </div>
  );
}
